import type { FC, ReactNode, PropsWithChildren } from 'react'

import styled from '@emotion/styled'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import IconWrapper from '@segment/matcha/components/IconWrapper'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import { DataIcon, UserIcon, CompliantIcon } from '@segment/matcha/components/Icons/Brandbook'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

type ColData = {
  icon: ReactNode
  heading: string
  description: string
}

const copy = {
  heading: `Take a proactive approach to privacy`,
  description: `Get real-time visibility into what personal information you’re collecting, where you’re collecting it from, and where you’re sending it. Then, set rules to automatically protect it.`,
}

const columnTrackData: ColData[] = [
  {
    icon: <DataIcon />,
    heading: `Customer data discovery`,
    description: `Automatically detect and classify personal information in real time instead of wasting months manually creating an inventory.`,
  },
  {
    icon: <CompliantIcon />,
    heading: `Privacy controls`,
    description: `Set rules to proactively block data to comply with your company’s privacy policy and requirements.`,
  },
  {
    icon: <UserIcon />,
    heading: `Subject rights management`,
    description: `Honor data subject rights requests at scale with automated deletion and suppression across Segment and your connected stack.`,
  },
]

const TakeAProactiveApproach: FC<PropsWithChildren<unknown>> = () => {
  return (
    <StyledSectionBase variant="centered" copyVariant="narrow">
      <SectionCopy>
        <CopyTextBase centered={true}>
          <CopyText.Heading tag="h2">{copy.heading}</CopyText.Heading>
          <CopyText.Description tag="p">{copy.description}</CopyText.Description>
        </CopyTextBase>
      </SectionCopy>
      <Section.Contents>
        <StyledColumnTrack columns={1} overrides={{ lg: 3 }} center={false}>
          {columnTrackData.map(({ heading, description, icon }) => (
            <FeatureBoxBase alignment="left" key={heading} headerPosition="top">
              <FeatureBox.Header>
                <StyledIconWrapper elevation={1} variant="circle">
                  {icon}
                </StyledIconWrapper>
              </FeatureBox.Header>
              <FeatureBox.Body>
                <FeatureBox.Title variant="h4">{heading}</FeatureBox.Title>
                <FeatureBox.Text>{description}</FeatureBox.Text>
              </FeatureBox.Body>
            </FeatureBoxBase>
          ))}
        </StyledColumnTrack>
      </Section.Contents>
    </StyledSectionBase>
  )
}

const StyledSectionBase = styled(SectionBase)`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-left: 80px;
    padding-right: 80px;
  }
`

const StyledColumnTrack = styled(ColumnTrack)`
  align-items: flex-start;
`

const SectionCopy = styled(Section.Copy)`
  margin: 0 auto;
  max-width: 844px;
`

const StyledIconWrapper = styled(IconWrapper)``

export default TakeAProactiveApproach
